/**
 * Helpers that map the active bot source + its capabilities to the badge copy
 * and popover copy displayed next to the source on the dashboard / source tile.
 * Centralised here so the label, popover and visual treatment stay in lockstep.
 *
 * IMPORTANT: Cloudflare and Fastly degrade DIFFERENTLY when Bot Management
 * (or the equivalent plan tier) is off — keep them in separate branches.
 *
 *   Cloudflare without BM — full per-bot / per-URL / per-status breakdown
 *     still works (this is what `bot_visits` is populated with), only the
 *     bot identification falls back from Detection ID to UA substring match.
 *     The numbers are reliable. → `neutral` modifier (informational, not a
 *     warning). And in Cloudflare there is NO separate Bot Management module
 *     to switch on — Detection IDs come bundled with higher plans
 *     (Business / Enterprise). The CTA is therefore a plan-tier link, not a
 *     "turn on Bot Management" link.
 *
 *   Fastly without BM — the only count we can read is the all-bots counter
 *     (`bot_edge_requests_detected_count`), which includes Googlebot /
 *     scrapers / monitoring on top of AI. The user can be genuinely misled
 *     by the headline number. → `coarse` modifier (warning). And Fastly's
 *     Bot Management IS a real toggleable feature — the CTA is a direct
 *     link to Fastly's Bot Management docs so the user can enable it.
 */

export type BotSourceKind = 'fastly' | 'cloudflare' | 'log' | null;

export interface DataQualityState {
  source: BotSourceKind;
  /**
   * `true` — source has Bot Management on (Fastly with BM, or Cloudflare
   *          Business+ with detection IDs).
   * `false` — source detected but Bot Management is off.
   * `null` — capability not applicable (e.g. log upload) or not yet known.
   */
  supportsBotManagement: boolean | null;
}

export interface BadgeCopy {
  labelKey: string;
  /**
   * `verified` (green) — full precision / accurate AI scope.
   * `neutral`  (blue)  — still useful, no caveat the user must act on.
   * `coarse`   (amber) — the user could misread the numbers.
   */
  modifier: 'verified' | 'neutral' | 'coarse';
}

export interface PopoverCopy {
  titleKey: string;
  bodyKey: string;
  /** Primary call-to-action label key. Omitted when no action makes sense. */
  ctaKey?: string;
  /** Absolute URL the CTA links to. Required if `ctaKey` is set. */
  ctaHref?: string;
  /** Subtle secondary note (e.g. "for per-bot detail also try …"). */
  secondaryKey?: string;
}

/**
 * Public Cloudflare plans page — used for "see Cloudflare plans" CTA in the
 * popover. Centralised so we don't sprinkle URLs around the codebase.
 */
const CLOUDFLARE_PLANS_URL = 'https://www.cloudflare.com/plans/';

/**
 * Fastly Bot Management product page — the action-oriented landing for users
 * who don't yet have Bot Management enabled. Better than docs.fastly.com
 * because it explains the product first.
 */
const FASTLY_BOT_MANAGEMENT_URL = 'https://www.fastly.com/products/bot-management';

export const getBadgeCopy = (state: DataQualityState): BadgeCopy | null => {
  if (state.source === null) return null;

  if (state.source === 'log') {
    return { labelKey: 'botsActivity.dataQuality.badge.log', modifier: 'verified' };
  }
  if (state.supportsBotManagement === null) return null;

  if (state.source === 'fastly') {
    return state.supportsBotManagement
      ? { labelKey: 'botsActivity.dataQuality.badge.fastlyBmOn', modifier: 'verified' }
      : { labelKey: 'botsActivity.dataQuality.badge.fastlyBmOff', modifier: 'coarse' };
  }
  // cloudflare
  return state.supportsBotManagement
    ? { labelKey: 'botsActivity.dataQuality.badge.cloudflareBmOn', modifier: 'verified' }
    : { labelKey: 'botsActivity.dataQuality.badge.cloudflareBmOff', modifier: 'neutral' };
};

export const getPopoverCopy = (state: DataQualityState): PopoverCopy | null => {
  if (state.source === null) return null;

  if (state.source === 'log') {
    return {
      titleKey: 'botsActivity.dataQuality.popover.log.title',
      bodyKey: 'botsActivity.dataQuality.popover.log.body',
      secondaryKey: 'botsActivity.dataQuality.popover.log.secondary',
    };
  }
  if (state.supportsBotManagement === null) return null;

  if (state.source === 'fastly') {
    if (state.supportsBotManagement) {
      return {
        titleKey: 'botsActivity.dataQuality.popover.fastlyBmOn.title',
        bodyKey: 'botsActivity.dataQuality.popover.fastlyBmOn.body',
        secondaryKey: 'botsActivity.dataQuality.popover.fastlyBmOn.secondary',
      };
    }
    return {
      titleKey: 'botsActivity.dataQuality.popover.fastlyBmOff.title',
      bodyKey: 'botsActivity.dataQuality.popover.fastlyBmOff.body',
      ctaKey: 'botsActivity.dataQuality.popover.fastlyBmOff.cta',
      ctaHref: FASTLY_BOT_MANAGEMENT_URL,
      secondaryKey: 'botsActivity.dataQuality.popover.fastlyBmOff.secondary',
    };
  }

  // cloudflare
  if (state.supportsBotManagement) {
    return {
      titleKey: 'botsActivity.dataQuality.popover.cloudflareBmOn.title',
      bodyKey: 'botsActivity.dataQuality.popover.cloudflareBmOn.body',
    };
  }
  return {
    titleKey: 'botsActivity.dataQuality.popover.cloudflareBmOff.title',
    bodyKey: 'botsActivity.dataQuality.popover.cloudflareBmOff.body',
    ctaKey: 'botsActivity.dataQuality.popover.cloudflareBmOff.cta',
    ctaHref: CLOUDFLARE_PLANS_URL,
  };
};
